import type { NavigateFunction } from 'react-router-dom'

import { IconTile } from '@buffer-mono/popcorn'

import { capitalize } from '~publish/helpers/capitalize'
import { buildUrlFromSavedView } from '~publish/helpers/savedViews'

import type { SavedView } from '../../MainSidebar/components/SavedViewsSection/hooks'
import { savedViewIdentity } from '../../MainSidebar/components/SavedViewsSection/savedViewIdentity'
import { PRIORITIES } from '../constants'
import { createAction } from './createAction'
import type { CommandBarAction } from './types'

const SAVED_VIEW_SECTION_LABELS: Record<SavedView['section'], string> = {
  publish: 'Publish',
  community: 'Community',
  create: 'Create',
  insights: 'Insights',
}

/**
 * Builds the secondary text shown beneath a saved view's name in the command
 * bar so views that share a `name` can be told apart before selection. Derived
 * from durable saved-view metadata: the human-readable `section`, qualified by
 * the `view` when present (e.g. `Publish · Queue`). Falls back to the section
 * alone when a view isn't set, so a result never renders empty.
 *
 * `view` is typed as an open `string`, but its canonical values are all single
 * lowercase words, so `capitalize` renders them cleanly. A multi-word or
 * camelCase slug would need a label map rather than this naive capitalize.
 */
export function describeSavedView(
  view: Pick<SavedView, 'section' | 'view'>,
): string {
  const section = SAVED_VIEW_SECTION_LABELS[view.section]
  return view.view ? `${section} · ${capitalize(view.view)}` : section
}

/**
 * Options for building saved-view command bar actions.
 */
interface BuildSavedViewActionsOptions {
  /** Saved views to render, in server-provided order */
  savedViews: SavedView[]
  /** React Router navigate function */
  navigate: NavigateFunction
  /** Called with the selected view before navigation (used for analytics) */
  onSelect: (view: SavedView) => void
  /**
   * The organization's currently valid channel IDs. Stale channels are dropped
   * before navigating so a removed channel can't dead-end the route.
   */
  validChannelIds?: Iterable<string>
}

/**
 * Builds a command bar action per saved view. Each action navigates via
 * `buildUrlFromSavedView` and calls the `onSelect` callback so the caller
 * can fire the `Saved View Loaded` analytics event with the right source.
 */
export function buildSavedViewActions({
  savedViews,
  navigate,
  onSelect,
  validChannelIds,
}: BuildSavedViewActionsOptions): CommandBarAction[] {
  const actions: CommandBarAction[] = []

  for (const view of savedViews) {
    const identity = savedViewIdentity(view)
    actions.push(
      createAction({
        id: `saved-view-${view.id}`,
        name: view.name,
        description: describeSavedView(view),
        keywords: [view.name.toLowerCase(), 'saved view', 'view', 'bookmark'],
        icon: (
          <IconTile
            icon={identity.icon}
            color={identity.color}
            appearance="plain"
          />
        ),
        section: 'saved-views',
        priority: PRIORITIES.savedViews,
        perform: () => {
          onSelect(view)
          navigate(buildUrlFromSavedView(view, validChannelIds))
        },
      }),
    )
  }

  return actions
}
